import { forwardRef, memo, type ReactNode } from 'react';
import clsx from 'clsx';
// Material UI imports
import Box from '@mui/material/Box';
import Grid, { type GridProps } from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
// local imports
import GridBox from '../mixins/GridBox';
// SCSS imports
import { overlayDefault } from '../styles/modules/Overlay.module.scss';
import { shadyBg } from '../styles/modules/ItemsGrid.module.scss';
import { container, compactContainer, titleRow, footerRow } from './CardDeck.module.scss';

type CardDeckProps = {
  // React built-in
  readonly children?: ReactNode;
  // attributes
  readonly title?: ReactNode | null;
  readonly footer?: ReactNode | null;
  readonly dark?: boolean | null;
  readonly shady?: boolean | null;
  readonly centered?: boolean | null;
  readonly compact?: boolean;
  readonly pending?: boolean | null;
  readonly sx?: GridProps['sx'];
}

const CardDeck = forwardRef<HTMLDivElement, CardDeckProps>(({
  children,
  title,
  footer,
  dark = false,
  shady = false,
  centered = false,
  compact = false,
  pending = false,
  sx
}, ref) => {
  const content = dark || shady ? (
    <GridBox
        ref={pending ? undefined : ref}
        container
        className={clsx(compact ? compactContainer : container, {
          [shadyBg]: shady
        })}
        bgcolor={dark ? 'background.card' : undefined}
        justifyContent={centered ? 'center' : undefined}
        alignItems="stretch"
        sx={sx}
    >
      {title ? (
        <Grid container size={12} className={titleRow}>
          {title}
        </Grid>
      ) : undefined}
      {children}
      {footer ? (
        <Grid container size={12} className={footerRow}>
          {footer}
        </Grid>
      ) : undefined}
    </GridBox>
  ) : (
    <Grid
        ref={pending ? undefined : ref}
        container
        className={compact ? compactContainer : container}
        justifyContent={centered ? 'center' : undefined}
        alignItems="stretch"
    >
      {title ? (
        <Grid container size={12} className={titleRow}>
          {title}
        </Grid>
      ) : undefined}
      {children}
      {footer ? (
        <Grid container size={12} className={footerRow}>
          {footer}
        </Grid>
      ) : undefined}
      {}
    </Grid>
  );
  return pending ? (
    <Box
        ref={ref}
        flexGrow={1}
        display="flex"
        flexDirection="column"
        position="relative"
    >
      {content}
      <Box className={overlayDefault}>
        <LinearProgress/>
      </Box>
    </Box>
  ) : content;
});

CardDeck.displayName = 'CardDeck';

export default memo(CardDeck);
